// Text measurement and label placement for the SVG charts.
//
// Chart labels are positioned in viewBox coordinates, so a label that fits in
// English can overflow the chart, collide with a neighbour or disappear off
// the edge once it is translated: "Training" becomes "Entrenamiento", "200K
// copies" becomes "200 000 copias". These helpers let a chart budget the space
// a label actually needs instead of assuming the English string's width.
//
// Every chart label uses Tailwind's default `font-mono` stack (ui-monospace,
// SFMono-Regular, Menlo, Consolas, ...), where each glyph advances by the same
// fraction of the font size regardless of which character it is. Measured in
// the browser across sizes and strings, that fraction is 0.6021 — Menlo's
// exact advance (1233/2048), and at least as wide as the other fonts in the
// stack, so on platforms that fall further down it these widths over-budget
// rather than overflow. Widths can therefore be computed arithmetically:
// nothing here touches the DOM, which keeps the charts server-rendered and
// free of the layout shift a measure-then-reposition pass would introduce.
// If `fontFamily.mono` is ever overridden in tailwind.config.ts, this
// constant must be re-measured against a font no narrower than the new one.

export const MONO_ADVANCE = 0.6021;

export type TextAnchor = 'start' | 'middle' | 'end';

/**
 * Rendered width, in viewBox units, of `text` set in the monospace stack.
 *
 * Counts UTF-16 code units, which equals glyph count for the Latin-script
 * languages the site is translated into. A language whose glyphs are not one
 * code unit and one monospace cell each — CJK's double-width characters,
 * combining accents, anything outside the Basic Multilingual Plane — would
 * need real segmentation and per-glyph advances here first.
 */
export function textWidth(text: string, fontSize: number): number {
  return text.length * fontSize * MONO_ADVANCE;
}

/** Width of the longest line in a block of text. */
export function blockWidth(lines: string[], fontSize: number): number {
  return lines.reduce((widest, line) => Math.max(widest, textWidth(line, fontSize)), 0);
}

/**
 * The largest font size up to `preferred` at which `text` fits in `maxWidth`.
 *
 * For headings and other single-line text that has nowhere to wrap to.
 */
export function fitFontSize(
  text: string,
  maxWidth: number,
  preferred: number,
  minimum: number
): number {
  if (textWidth(text, preferred) <= maxWidth) return preferred;
  const fitted = maxWidth / (text.length * MONO_ADVANCE);
  return Math.max(minimum, Math.floor(fitted * 10) / 10);
}

/** The largest size the chart titles' responsive classes resolve to. */
const RESPONSIVE_TITLE_MAX = 28;

/**
 * Sizing for a chart title, which has nowhere to wrap to.
 *
 * A title that fits at every one of its responsive sizes keeps them, which is
 * what `fits` reports; the caller applies its own class string, because
 * Tailwind only generates classes it can see in the files it scans. A title
 * that does not fit — which is what a translation half again as long as the
 * English original produces — is set at a single size that does.
 */
export function fitTitle(
  text: string,
  maxWidth: number,
  minimum = 14
): { fontSize: number; fits: boolean } {
  const fontSize = fitFontSize(text, maxWidth, RESPONSIVE_TITLE_MAX, minimum);
  return { fontSize, fits: fontSize === RESPONSIVE_TITLE_MAX };
}

/**
 * Break `text` into lines that each fit within `maxWidth`.
 *
 * Explicit newlines are kept as hard breaks, so a translator can still choose
 * where a label divides; wrapping only intervenes where they did not. A single
 * word longer than `maxWidth` is left on its own line rather than being cut.
 */
export function wrapText(text: string, maxWidth: number, fontSize: number): string[] {
  return text.split('\n').flatMap((paragraph) => {
    const words = paragraph.split(' ').filter((word) => word.length > 0);
    if (words.length === 0) return [paragraph];

    const lines: string[] = [];
    let line = words[0];
    for (const word of words.slice(1)) {
      const candidate = `${line} ${word}`;
      if (textWidth(candidate, fontSize) <= maxWidth) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    }
    lines.push(line);
    return lines;
  });
}

/**
 * Shift `x` so that text of `width` anchored at it stays within [min, max].
 *
 * Used as a backstop for text that cannot be wrapped any narrower.
 */
export function clampAnchoredX(
  x: number,
  anchor: TextAnchor,
  width: number,
  min: number,
  max: number
): number {
  const left = anchor === 'start' ? x : anchor === 'end' ? x - width : x - width / 2;
  const shift =
    left < min ? min - left : left + width > max ? max - (left + width) : 0;
  return x + shift;
}

/** Baseline offsets that centre `count` lines on the anchor point. */
export function centeredLineOffsets(count: number, lineHeight: number): number[] {
  return Array.from({ length: count }, (_, i) => (i - (count - 1) / 2) * lineHeight);
}

export interface RadialLabel {
  x: number;
  y: number;
  anchor: TextAnchor;
  lines: string[];
  /** Baseline offset for each line, relative to `y`. */
  lineOffsets: number[];
}

/**
 * Place a pie or donut label outside the slice it belongs to.
 *
 * The anchor follows the slice's direction rather than its text, so it stays
 * correct in every language: labels on the left of the circle are right
 * aligned, labels on the right are left aligned, and labels near the top or
 * bottom are centred. The label is then wrapped to the space between its
 * anchor point and the edge of the chart's own horizontal bounds.
 */
export function layoutRadialLabel({
  centerX,
  centerY,
  angle,
  radius,
  clearRadius,
  text,
  fontSize,
  lineHeight,
  bounds,
}: {
  centerX: number;
  centerY: number;
  /** Mid-angle of the slice, in radians, measured as in SVG (0 = due right). */
  angle: number;
  /** Distance from the centre at which the label sits. */
  radius: number;
  /** Radius the label must stay clear of, i.e. the edge of the pie itself. */
  clearRadius: number;
  text: string;
  fontSize: number;
  lineHeight: number;
  /** Horizontal range the label must stay inside, in viewBox units. */
  bounds: { min: number; max: number };
}): RadialLabel {
  const horizontal = Math.cos(angle);
  const vertical = Math.sin(angle);
  // Slices pointing nearly straight up or down have no side to sit on.
  const anchor: TextAnchor =
    Math.abs(horizontal) < 0.2 ? 'middle' : horizontal > 0 ? 'start' : 'end';

  const x = centerX + horizontal * radius;
  const y = centerY + vertical * radius;

  const available =
    anchor === 'start'
      ? bounds.max - x
      : anchor === 'end'
        ? x - bounds.min
        : 2 * Math.min(x - bounds.min, bounds.max - x);

  const lines = wrapText(text, available, fontSize);
  const lineOffsets = centeredLineOffsets(lines.length, lineHeight);
  const width = blockWidth(lines, fontSize);
  const halfHeight = (lines.length - 1) * lineHeight * 0.5 + fontSize * 0.5;

  // A label wrapped onto several lines is taller than the single line the
  // radius was chosen for, so its corner can reach back over the pie. Move it
  // outwards along its own angle until the whole block clears. A label that
  // already clears is not moved.
  let distance = radius;
  for (let step = 0; step < 64; step += 1) {
    const cx = centerX + horizontal * distance;
    const cy = centerY + vertical * distance;
    const left = anchor === 'start' ? cx : anchor === 'end' ? cx - width : cx - width / 2;
    // Nearest point of the label's box to the centre of the pie.
    const nearestX = Math.min(Math.max(centerX, left), left + width);
    const nearestY = Math.min(Math.max(centerY, cy - halfHeight), cy + halfHeight);
    if (Math.hypot(nearestX - centerX, nearestY - centerY) >= clearRadius) break;
    distance += 1;
  }

  return {
    x: clampAnchoredX(
      centerX + horizontal * distance,
      anchor,
      width,
      bounds.min,
      bounds.max
    ),
    y: centerY + vertical * distance,
    anchor,
    lines,
    lineOffsets,
  };
}

export interface Callout {
  box: { x: number; y: number; width: number; height: number };
  /** Centre line of the box; the text is anchored "middle" on it. */
  textX: number;
  /** Baseline of the first line of text. */
  textY: number;
  lines: string[];
}

/**
 * Size and place a boxed annotation next to a point on a chart.
 *
 * The box is sized from the text it holds rather than from a fixed width, and
 * flips to the opposite side of the point when the preferred side would push
 * it outside the plotting area.
 */
export function layoutCallout({
  pointX,
  pointY,
  text,
  fontSize,
  lineHeight,
  padding,
  gap,
  prefer,
  bounds,
}: {
  pointX: number;
  pointY: number;
  text: string;
  fontSize: number;
  lineHeight: number;
  padding: number;
  /** Distance between the point and the nearest edge of the box. */
  gap: number;
  prefer: { horizontal: 'left' | 'right'; vertical: 'above' | 'below' };
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}): Callout {
  const maxWidth = bounds.maxX - bounds.minX - 2 * padding - gap;
  const lines = wrapText(text, maxWidth, fontSize);

  const width = blockWidth(lines, fontSize) + 2 * padding;
  const height = lines.length * lineHeight + 2 * padding;

  const leftX = pointX - gap - width;
  const rightX = pointX + gap;
  const fitsLeft = leftX >= bounds.minX;
  const fitsRight = rightX + width <= bounds.maxX;
  const x =
    prefer.horizontal === 'left'
      ? fitsLeft || !fitsRight
        ? leftX
        : rightX
      : fitsRight || !fitsLeft
        ? rightX
        : leftX;

  const aboveY = pointY - gap - height;
  const belowY = pointY + gap;
  const fitsAbove = aboveY >= bounds.minY;
  const fitsBelow = belowY + height <= bounds.maxY;
  const y =
    prefer.vertical === 'above'
      ? fitsAbove || !fitsBelow
        ? aboveY
        : belowY
      : fitsBelow || !fitsAbove
        ? belowY
        : aboveY;

  const boxX = Math.min(Math.max(x, bounds.minX), bounds.maxX - width);
  const boxY = Math.min(Math.max(y, bounds.minY), bounds.maxY - height);

  return {
    box: { x: boxX, y: boxY, width, height },
    textX: boxX + width / 2,
    textY: boxY + padding + fontSize * 0.8,
    lines,
  };
}
